export interface EditableManuscriptChapter {
  readonly order: number;
  readonly title: string;
  readonly content: string;
  readonly word_count: number;
}

export interface ParsedManuscript {
  readonly frontMatter: string;
  readonly chapters: readonly EditableManuscriptChapter[];
}

const CHAPTER_HEADING = /^#\s+Chapter\s+(\d+)\s*:\s*(.+)$/iu;

export function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/u).length;
}

export function parseManuscriptMarkdown(markdown: string): ParsedManuscript {
  const normalized = markdown.replace(/\r\n/g, "\n");
  const lines = normalized.split("\n");
  const chapters: EditableManuscriptChapter[] = [];
  const frontMatterLines: string[] = [];
  let current:
    | { order: number; title: string; lines: string[] }
    | null = null;

  for (const line of lines) {
    const match = CHAPTER_HEADING.exec(line.trim());
    if (match) {
      if (current) {
        chapters.push(buildChapter(current));
      }
      current = {
        order: Number.parseInt(match[1], 10),
        title: match[2].trim(),
        lines: [],
      };
      continue;
    }

    if (current) {
      current.lines.push(line);
    } else {
      frontMatterLines.push(line);
    }
  }

  if (current) {
    chapters.push(buildChapter(current));
  }

  return {
    frontMatter: frontMatterLines.join("\n").trim(),
    chapters: chapters.sort((a, b) => a.order - b.order),
  };
}

export function serializeManuscriptMarkdown(input: {
  readonly title: string;
  readonly authorName: string;
  readonly chapters: readonly EditableManuscriptChapter[];
}): string {
  const year = new Date().getUTCFullYear();
  const frontMatter = [
    `# ${input.title.trim() || "Untitled Manuscript"}`,
    "",
    `## ${input.authorName.trim() || "Author"}`,
    "",
    "---",
    "",
    `Copyright (c) ${year} ${input.authorName.trim() || "Author"}.`,
    "",
    "All rights reserved.",
    "",
    "Published by Pull Press.",
    "",
    "---",
  ];

  const chapterMarkdown = input.chapters
    .slice()
    .sort((a, b) => a.order - b.order)
    .flatMap((chapter) => [
      "",
      `# Chapter ${chapter.order}: ${chapter.title.trim() || `Chapter ${chapter.order}`}`,
      "",
      chapter.content.trim(),
      "",
      "---",
    ]);

  return [...frontMatter, ...chapterMarkdown, ""].join("\n");
}

function buildChapter(input: { order: number; title: string; lines: readonly string[] }): EditableManuscriptChapter {
  const content = trimChapterSeparators(input.lines.join("\n"));
  return {
    order: input.order,
    title: input.title,
    content,
    word_count: countWords(content),
  };
}

function trimChapterSeparators(text: string): string {
  return text
    .replace(/^\s*-{3,}\s*/u, "")
    .replace(/\s*-{3,}\s*$/u, "")
    .trim();
}
